import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useInView } from 'motion/react';
import { ArrowLeft, Clock, Eye, ArrowRight, User, Star, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Layout from '../components/layout/Layout';
import LazyImage from '../components/ui/LazyImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 60 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.div>
  );
};

const BlogCard = ({ post, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });
  const imageUrl = post.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
  const readTime = post.read_time || 5;
  const categoryDisplay = post.category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Blog';

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }} transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}>
      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
        <motion.article className="group card-fresh overflow-hidden" whileHover={{ y: -8 }}>
          <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden">
            <LazyImage src={post.featured_image} alt={post.title} className="w-full h-full group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">{categoryDisplay}</span>
              {post.is_featured && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1"><Star size={10} fill="currentColor" /> Featured</span>
              )}
            </div>
          </div>
          <div className="relative z-10">
            <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2">{post.title}</h3>
            {post.excerpt && <p className="text-[#5A5A5A] text-sm mb-4 line-clamp-2">{post.excerpt}</p>}
            <div className="flex items-center gap-4 text-[#8A8A8A] text-sm mb-4">
              <span className="flex items-center gap-1"><Clock size={14} />{readTime} min</span>
              <span className="flex items-center gap-1"><Eye size={14} />{post.views || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-[#5A5A5A] text-sm"><User size={14} />{post.author_name || 'Buzz Craft Maven Team'}</span>
              <ArrowRight size={18} className="text-[#8A8A8A] group-hover:text-[#FF6B35] group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </motion.article>
      </Link>
    </motion.div>
  );
};

export default function BlogTag() {
  const { tag } = useParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [explorePosts, setExplorePosts] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/public/blog/tags/${tag}`).then(r => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/public/blog/tags`).then(r => r.ok ? r.json() : []),
      fetch(`${API_URL}/api/public/blog/explore?exclude_tag=${tag}&limit=4`).then(r => r.ok ? r.json() : [])
    ]).then(([tagData, tags, explore]) => {
      if (tagData) {
        setPosts(tagData.posts || []);
        setTotal(tagData.total || 0);
      }
      setAllTags(tags);
      setExplorePosts(explore);
    }).catch(console.error).finally(() => setLoading(false));
  }, [tag]);

  const tagDisplay = tag?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
          <motion.div className="w-12 h-12 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full" animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>#{tagDisplay} Articles | Buzz Craft Maven Blog</title>
        <meta name="description" content={`Browse all articles tagged with "${tagDisplay}" on Buzz Craft Maven.`} />
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div className="absolute top-20 right-[15%] w-[400px] h-[400px] sun-orb sun-orb-primary" animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 8, repeat: Infinity }} />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#5A5A5A] hover:text-[#FF6B35] transition-colors mb-8" data-testid="back-to-blog">
            <ArrowLeft size={18} /> Back to Blog
          </Link>
          <TextReveal>
            <div className="flex items-center gap-3 mb-4">
              <Tag className="text-[#FF6B35]" size={24} />
              <span className="text-[#FF6B35] text-sm font-medium uppercase tracking-widest">Tag</span>
            </div>
          </TextReveal>
          <TextReveal delay={0.1}>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.05] mb-4" data-testid="tag-title">#{tagDisplay}</h1>
          </TextReveal>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-[#8A8A8A]">
            {total} {total === 1 ? 'article' : 'articles'} tagged
          </motion.p>
        </div>
      </section>

      {/* Tag Cloud */}
      {allTags.length > 1 && (
        <section className="py-4 bg-white border-y border-[#FF6B35]/10 sticky top-20 z-40">
          <div className="container mx-auto px-6 md:px-12">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/blog" className="px-4 py-2 rounded-full text-sm bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30 transition-all" data-testid="filter-all">All</Link>
              {allTags.slice(0, 12).map((t) => (
                <Link key={t.slug} to={`/blog/tag/${t.slug}`} data-testid={`filter-tag-${t.slug}`}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${t.slug === tag || t.name.toLowerCase() === tag.toLowerCase() ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white font-medium shadow-lg shadow-orange-500/20' : 'bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'}`}>
                  #{t.name} ({t.post_count})
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts Grid + Explore Sidebar */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8">
            {/* Main Grid */}
            <div className="col-span-12 lg:col-span-9">
              {posts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tag-posts-grid">
                  {posts.map((post, index) => (
                    <BlogCard key={post.id} post={post} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-[#5A5A5A] text-lg">No articles found with this tag yet.</p>
                  <Link to="/blog" className="inline-flex items-center gap-2 mt-6 text-[#FF6B35] hover:underline"><ArrowLeft size={16} /> Browse all articles</Link>
                </div>
              )}
            </div>

            {/* Explore Sidebar */}
            {explorePosts.length > 0 && (
              <div className="col-span-12 lg:col-span-3">
                <div className="lg:sticky lg:top-32 space-y-4">
                  <h3 className="font-display text-lg font-semibold text-[#2D2D2D] mb-2" data-testid="explore-sidebar-title">You Might Also Like</h3>
                  <p className="text-[#8A8A8A] text-sm mb-4">Popular articles from other topics</p>
                  {explorePosts.map((ep) => {
                    const catName = ep.category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '';
                    return (
                      <Link key={ep.slug} to={`/blog/${ep.slug}`} data-testid={`explore-${ep.slug}`}
                        className="block p-4 bg-white rounded-xl border border-[#FF6B35]/10 hover:border-[#FF6B35]/30 hover:shadow-sm transition-all group">
                        <span className="text-[#FF6B35] text-xs font-medium uppercase tracking-wider">{catName}</span>
                        <h4 className="text-[#2D2D2D] font-medium text-sm mt-1 group-hover:text-[#FF6B35] transition-colors line-clamp-2">{ep.title}</h4>
                        <div className="flex items-center gap-3 mt-2 text-[#8A8A8A] text-xs">
                          <span className="flex items-center gap-1"><Clock size={10} />{ep.read_time || 5} min</span>
                          <span className="flex items-center gap-1"><Eye size={10} />{ep.views || 0}</span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
